import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Search } from "lucide-react";
import PageShell from "../components/PageShell";
import EnquireModal from "../components/EnquireModal";
import { Input } from "../components/ui/input";
import { useContent } from "../contexts/ContentContext";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogList() {
  const { content } = useContent();
  const posts = useMemo(() => content.blogPosts || [], [content.blogPosts]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const sorted = [...posts].sort(
      (a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0)
    );
    if (!term) return sorted;
    return sorted.filter((p) =>
      [p.title, p.excerpt, p.category, p.author]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [posts, q]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <PageShell
        title="Zenova Blog"
        subtitle="Guides, insights and news on clean energy for Indian homes and businesses."
        onEnquire={() => setOpen(true)}
      >
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles by keyword…"
            className="pl-10 h-11"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            No posts match your search.
          </div>
        ) : (
          <>
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="group block rounded-3xl overflow-hidden bg-white border border-slate-200 hover:border-orange-300 hover:shadow-xl transition-all mb-10"
              >
                <div className="grid md:grid-cols-2">
                  <div className="relative aspect-video md:aspect-auto md:h-full overflow-hidden">
                    <img
                      src={featured.cover}
                      alt={featured.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    {featured.category ? (
                      <span className="inline-block self-start px-2.5 py-1 rounded-full bg-orange-50 text-orange-600 text-[11px] font-bold uppercase tracking-wider mb-3">
                        {featured.category}
                      </span>
                    ) : null}
                    <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 group-hover:text-[#0a4f47] transition-colors leading-tight">
                      {featured.title}
                    </h2>
                    <p className="mt-3 text-slate-600 leading-relaxed">
                      {featured.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(featured.publishedAt)}
                      </span>
                      {featured.author ? <span>· {featured.author}</span> : null}
                    </div>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-orange-600 font-semibold group-hover:gap-2.5 transition-all">
                      Read article <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((p) => (
                  <Link
                    key={p.slug || p.id}
                    to={`/blog/${p.slug}`}
                    className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-orange-300 hover:shadow-lg hover:-translate-y-1 transition-all"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={p.cover}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      {p.category ? (
                        <span className="self-start px-2 py-0.5 rounded-full bg-emerald-50 text-[#0a4f47] text-[10px] font-bold uppercase tracking-wider mb-2">
                          {p.category}
                        </span>
                      ) : null}
                      <h3 className="font-bold text-slate-900 leading-snug group-hover:text-[#0a4f47] transition-colors">
                        {p.title}
                      </h3>
                      <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(p.publishedAt)}
                        </span>
                        <ArrowRight className="w-4 h-4 text-orange-500 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </PageShell>
      <EnquireModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
